<div class="general-settings d-sm-flex flex-column" [formGroup]="settingsForm">
    <h3>Assistant Settings</h3>

    <!-- Instructions Fields -->
    <mat-form-field appearance="fill" class="full-width">
        <mat-label>Instructions</mat-label>
        <textarea matInput formControlName="instructions" placeholder="Enter instructions" rows="10"></textarea>
        @if (settingsForm.get('instructions')?.invalid && settingsForm.get('instructions')?.touched) {
            <mat-error>
                Instructions are required
            </mat-error>
        }
    </mat-form-field>
    <button mat-raised-button color="secondary" (click)="proposeImprovements()" [disabled]="isProposing">
        {{ isProposing ? 'Suggesting...' : 'Improve instructions' }}
    </button>

    <mat-slide-toggle formControlName="realTimeEnabled">Real-Time Enabled</mat-slide-toggle>

    <!-- Real-Time Voice Field -->
    @if (settingsForm.get('realTimeEnabled')?.value) {
        <mat-form-field appearance="fill" class="full-width">
            <mat-label>Real-Time Voice</mat-label>
            <mat-select formControlName="realTimeVoice" placeholder="Select a realtime voice">
                @for (voice of realtimeVoiceOptions; track voice) {
                    <mat-option [value]="voice">{{ voice | titlecase }}</mat-option>
                }
            </mat-select>
            <mat-hint>Recommended: Marin or Cedar.</mat-hint>
        </mat-form-field>
    }

    <mat-form-field appearance="fill" class="full-width">
        <mat-label>Model</mat-label>
        <mat-select formControlName="model" placeholder="Select a model">
            <mat-select-trigger>
                {{ displayModelValue(settingsForm.get('model')?.value) }}
            </mat-select-trigger>
            @if (legacyModelValue; as legacyModel) {
                <mat-option [value]="legacyModel" disabled>{{ legacyModel }} (legacy)</mat-option>
            }
            @for (option of modelOptions; track option.value) {
                <mat-option [value]="option.value">
                    {{ option.label }}
                    @if (!option.configured) {
                        <span class="text-muted">(not configured)</span>
                    }
                </mat-option>
            }
        </mat-select>
        @if (settingsForm.get('model')?.invalid && settingsForm.get('model')?.touched) {
            <mat-error>
                Model selection is required
            </mat-error>
        }
    </mat-form-field>

    <section class="response-delivery" aria-labelledby="response-delivery-title">
        <h4 id="response-delivery-title">Response delivery</h4>
        <dl>
            <div>
                <dt>Provider</dt>
                <dd>{{ effectiveProvider() }}</dd>
            </div>
            <div>
                <dt>Response format</dt>
                <dd>Structured assistant response v1</dd>
            </div>
            <div>
                <dt>Streaming</dt>
                <dd>Enabled with a safe non-streaming fallback</dd>
            </div>
            <div>
                <dt>Completion safety</dt>
                <dd>Content is saved before the final response is shown</dd>
            </div>
            <div>
                <dt>Recovery</dt>
                <dd>Uses the chatbot's localized error messages and bounded retries</dd>
            </div>
        </dl>
        <p>Provider follows the selected model. Safety limits are fixed to protect conversations and cannot be changed here.</p>
    </section>

    <!-- Reasoning Effort -->
    @if (shouldShowReasoningControls()) {
        <mat-form-field appearance="fill" class="full-width">
            <mat-label>Reasoning Effort</mat-label>
            <mat-select formControlName="reasoningEffort" placeholder="Select reasoning effort">
                @for (option of getReasoningEffortOptionsForModel(); track option) {
                    <mat-option [value]="option">
                        {{ option | titlecase }}
                    </mat-option>
                }
            </mat-select>
            <mat-hint>gpt-5.2 defaults to none. Higher effort improves quality but costs more tokens.</mat-hint>
    </mat-form-field>
    }

    <!-- Summary Verbosity -->
    @if (shouldShowReasoningControls()) {
        <mat-form-field appearance="fill" class="full-width">
            <mat-label>Reasoning Summary</mat-label>
            <mat-select formControlName="reasoningSummaryVerbosity" placeholder="Select summary verbosity">
                <mat-option [value]="''">Model default</mat-option>
                @for (option of summaryOptions; track option) {
                    <mat-option [value]="option">
                        {{ option | titlecase }}
                    </mat-option>
                }
            </mat-select>
            <mat-hint>Use auto unless debugging. Concise is reserved for computer-use preview models.</mat-hint>
    </mat-form-field>
    }

    <!-- Temperature Field -->
    @if (settingsForm.get('model')?.value !== 'o3-mini') {
        <mat-form-field appearance="fill" class="full-width">
            <mat-label>Temperature</mat-label>
            <input formControlName="temperature" matInput placeholder="Enter temperature value" type="number">
    </mat-form-field>
    }

    <!-- Tools -->
    <mat-slide-toggle formControlName="usePromptCaching">Enable Prompt Cache</mat-slide-toggle>
    <mat-slide-toggle formControlName="webSearch">Enable Web Search</mat-slide-toggle>
    @if (settingsForm.get('webSearch')?.value) {
        <mat-form-field appearance="fill" class="full-width">
            <mat-label>Web Search Domains</mat-label>
            <mat-select formControlName="webSearchDomains" multiple [disabled]="isDomainsLoading || !verifiedDomains.length">
                @for (domain of verifiedDomains; track domain) {
                    <mat-option [value]="domain">{{ domain }}</mat-option>
                }
            </mat-select>
            <mat-hint>Defaults to all verified domains in Shine Auth.</mat-hint>
            @if (isDomainsLoading) {
                <mat-hint>Loading verified domains...</mat-hint>
            }
            @if (!isDomainsLoading && !verifiedDomains.length) {
                <mat-hint>No verified domains configured in Shine Auth.</mat-hint>
            }
        </mat-form-field>
    }
    <mat-slide-toggle formControlName="codeInterpreter">Enable Code Interpreter</mat-slide-toggle>
</div>
